import { useCallback, useState } from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@holigral/shared/ui/components/ui/dialog"
import { Skeleton } from "@holigral/shared/ui/components/ui/skeleton"
import { Spinner } from "@holigral/shared/ui/components/ui/spinner"
import { useGetBillingSummaryQuery } from "./api"
import AmountSelector from "./AmountSelector"
import buildAmountOptions, { DEFAULT_AMOUNT_KEY } from "./amountOptions"
import FirstPurchaseForm from "./FirstPurchaseForm"
import formatMoney from "./formatMoney"
import RepeatPurchasePanel from "./RepeatPurchasePanel"
import StripeElementsProvider from "./StripeElementsProvider"
import useSettlementHandoff from "./useSettlementHandoff"
import type { AmountOptionKey, TopUpAction } from "./model"
import type { Notification } from "@/notification"
import { useT } from "@/i18n"

type Props = {
  readonly action: TopUpAction
  /** Credits the workspace is short of (0 for a standalone Billing top-up). */
  readonly shortfall: number
  readonly presentation: "inline" | "dialog"
  readonly open?: boolean
  readonly onOpenChange?: (open: boolean) => void
  readonly onComplete?: (settlement: Notification) => void
}

/**
 * The shared top-up surface (product.md §6.3), rendered in three places: inline
 * in the send flow, inline in the self-profile flow, and standalone on the
 * Billing page. Owner-only (spending money is an owner action, §4.6), so it can
 * safely read the owner summary. Never navigates.
 *
 * The settlement handoff lives here, not in the inner forms, so it survives the
 * first-purchase → repeat layout switch that happens once a saved card is
 * cached. While a payment is settling it shows a processing view with no pay
 * button, so the paid checkout can't be repeated.
 */
export default function TopUp({
  action,
  shortfall,
  presentation,
  open,
  onOpenChange,
  onComplete,
}: Props) {
  const { t } = useT("money")
  const { data: summary } = useGetBillingSummaryQuery()
  const [selectedKey, setSelectedKey] =
    useState<AmountOptionKey>(DEFAULT_AMOUNT_KEY)

  const handleComplete = useCallback(
    (settlement: Notification) => {
      toast.success(t("topUp.success"))
      onOpenChange?.(false)
      onComplete?.(settlement)
    },
    [t, onOpenChange, onComplete],
  )

  const { submitted, timedOut, awaitSettlement } =
    useSettlementHandoff(handleComplete)

  const renderBody = () => {
    if (!summary) {
      return <Skeleton className="h-40 w-full rounded-md" />
    }

    if (submitted) {
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-sm text-muted-foreground">
          <Spinner />
          <p>{timedOut ? t("topUp.settlementSlow") : t("topUp.processing")}</p>
        </div>
      )
    }

    const options = buildAmountOptions(
      shortfall,
      summary.unitPriceCents,
      summary.currency,
    )
    const selected =
      options.find((option) => option.key === selectedKey) ?? options[1]
    const card = summary.defaultPaymentMethod

    const payLabel =
      summary.billingReady && card
        ? t("topUp.payWithSaved", {
            total: formatMoney(selected.total),
            last4: card.last4,
          })
        : t("topUp.payNew", { total: formatMoney(selected.total) })

    return (
      <div className="flex flex-col gap-4">
        <AmountSelector
          options={options}
          value={selectedKey}
          onChange={setSelectedKey}
        />
        {summary.billingReady ? (
          <RepeatPurchasePanel
            credits={selected.credits}
            payLabel={payLabel}
            action={action}
            onSubmitted={awaitSettlement}
          />
        ) : (
          <StripeElementsProvider
            options={{
              mode: "payment",
              amount: selected.total.amount,
              currency: summary.currency,
              setupFutureUsage: "on_session",
            }}
          >
            <FirstPurchaseForm
              credits={selected.credits}
              payLabel={payLabel}
              action={action}
              onSubmitted={awaitSettlement}
            />
          </StripeElementsProvider>
        )}
      </div>
    )
  }

  if (presentation === "dialog") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("topUp.title")}</DialogTitle>
            <DialogDescription>{t("topUp.subtitle")}</DialogDescription>
          </DialogHeader>
          {renderBody()}
        </DialogContent>
      </Dialog>
    )
  }

  return renderBody()
}
